import { Suspense } from "react";
import { t } from "@/i18n-js/instance";
import { useThemeContext } from "@/react/providers";
import { lazyWithRetry } from "@circle-react/helpers/lazyRetry";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import { PlanFeature } from "../../SettingsApp/Plan/PlanSettings/PlanSelector/PlanFeature";
import BannerWrapper from "./shared/BannerWrapper";

const BrandedAppIllustration = lazyWithRetry(
  () =>
    // This chunk is lazy loaded because it should be treated as an image
    import(
      /* webpackChunkName: "BrandedAppIllustration" */ "./shared/BrandedAppIllustration"
    ),
);

const i18nRoot = "circle_plus.banners.new_plans_page";

interface NewPlansPageBannerProps {
  logoUrl?: string | null;
  appName?: string | null;
  brandColors?: {
    dark: string;
    light: string;
  };
}

const featureRows = [
  [
    `${i18nRoot}.features.branded_apps`,
    `${i18nRoot}.features.migration_service`,
    `${i18nRoot}.features.dedicated_launch_team`,
    `${i18nRoot}.features.design_studio`,
  ],
  [
    `${i18nRoot}.features.push_notifications`,
    `${i18nRoot}.features.in_app_purchases`,
    `${i18nRoot}.features.limits`,
    `${i18nRoot}.features.concierge_onboarding`,
  ],
];

export const NewPlansPageBanner = ({
  logoUrl,
  appName,
  brandColors,
}: NewPlansPageBannerProps) => {
  const { isDarkAppearance } = useThemeContext();

  const truncatedAppName =
    appName && appName.length > 20 ? `${appName.slice(0, 20)}...` : appName;

  return (
    <BannerWrapper>
      <div className="bg-plus-banner relative rounded-2xl bg-contain bg-right bg-no-repeat dark:bg-white">
        <div className="w-full px-10 py-8">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col">
              <div className="flex flex-row">
                <Typography.TitleMd color="text-white dark:text-slate-900">
                  {t(`${i18nRoot}.title`)}
                </Typography.TitleMd>
                <Typography.TitleMd color="text-gray-400">
                  {t(`${i18nRoot}.title_suffix`)}
                </Typography.TitleMd>
              </div>
              <Typography.BodySm color="text-zinc-300 dark:text-slate-700">
                {t([i18nRoot, "subtitle"])}
              </Typography.BodySm>
            </div>
            <div className="flex flex-row">
              <div className="mr-0 flex grow flex-row flex-wrap gap-2 sm:mr-[150px]">
                {featureRows.map((features, columnIndex) => (
                  <div
                    className="-mx-1 flex max-w-[300px] flex-wrap gap-2"
                    key={columnIndex}
                  >
                    {features.map((feature, index) => (
                      <div key={index + columnIndex * 4}>
                        <PlanFeature
                          feature={t(feature)}
                          isHighlighted
                          color="text-zinc-300 dark:text-slate-700"
                          LabelComponent={Typography.BodyXs}
                          highlightColor={
                            isDarkAppearance
                              ? brandColors?.light
                              : brandColors?.dark
                          }
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className="absolute bottom-0 right-8 hidden sm:block">
                <Suspense fallback={null}>
                  {truncatedAppName && (
                    <BrandedAppIllustration
                      appLogo={logoUrl}
                      appName={truncatedAppName}
                      appColors={brandColors}
                    />
                  )}
                </Suspense>
              </div>
            </div>

            <a
              href="https://circle.so/plus?utm_source=plans_page_banner"
              target="_blank"
              rel="noreferrer"
            >
              <Button variant="secondary-light" className="font-medium">
                {t(`${i18nRoot}.button`)}
              </Button>
            </a>
          </div>
        </div>
      </div>
    </BannerWrapper>
  );
};

export default NewPlansPageBanner;
